'use client'

import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from 'react'

const SESSION_KEY = 'seg_gate'

type InteractionGateContextValue = { hasInteracted: boolean }

const InteractionGateContext = createContext<InteractionGateContextValue>({
  hasInteracted: false,
})

export function useInteractionGate(): InteractionGateContextValue {
  return useContext(InteractionGateContext)
}

/**
 * Defers Segment events until the user first scrolls or clicks, eliminating
 * MTUs from pure bounce sessions. Uses sessionStorage so the gate stays open
 * across client-side navigations within the same tab.
 */
export function InteractionGateProvider({ children }: { children: ReactNode }) {
  const [hasInteracted, setHasInteracted] = useState(() => {
    if (typeof window === 'undefined') return false
    return sessionStorage.getItem(SESSION_KEY) === '1'
  })

  useEffect(() => {
    if (hasInteracted) return

    const handleInteraction = () => {
      // Remove both to prevent double-firing — scroll can also emit a click
      document.removeEventListener('click', handleInteraction)
      document.removeEventListener('scroll', handleInteraction)
      sessionStorage.setItem(SESSION_KEY, '1')
      setHasInteracted(true)
    }

    document.addEventListener('click', handleInteraction, { passive: true })
    document.addEventListener('scroll', handleInteraction, { passive: true })

    return () => {
      document.removeEventListener('click', handleInteraction)
      document.removeEventListener('scroll', handleInteraction)
    }
  }, [hasInteracted])

  return (
    <InteractionGateContext.Provider value={{ hasInteracted }}>
      {children}
    </InteractionGateContext.Provider>
  )
}
